/**
 * Putting alaw at the front of what the browser offers.
 *
 * Browsers offer Opus first and every European trunk speaks G.711, so unless the two are
 * made to agree the PBX transcodes every call — which costs roughly five to ten times a
 * pass-through call and is the difference, on a two-core box, between a hundred concurrent
 * calls and fifteen. Section 09 of the blueprint calls this out; the hardware doc puts a
 * number on it.
 *
 * The fix is free. G.711 costs 87 kbit/s per stream on a LAN where the office has a
 * gigabit, and it removes the CPU question at this scale entirely.
 *
 * Reordering the payload list is all that is needed: the codec chosen is the first one in
 * the offer that both ends support, so moving PCMA to the front of `m=audio` is enough.
 * Nothing is removed — a PBX that for some reason cannot do alaw still finds Opus behind
 * it, which is a worse call rather than no call.
 */

/** The static payload type for PCMA, which is alaw. Fixed by RFC 3551. */
const PCMA = '8';

/** And PCMU, mu-law, which is the same argument in North America. */
const PCMU = '0';

export function preferAlaw(sdp: string): string {
  // Line endings in SDP are CRLF by specification and LF in practice often enough that
  // splitting on either is the only version of this that works everywhere.
  const lines = sdp.split(/\r\n|\n/);

  const index = lines.findIndex((line) => line.startsWith('m=audio '));

  if (index < 0) return sdp;

  // m=audio <port> <proto> <payload> <payload> ...
  const parts = lines[index].split(' ');

  if (parts.length < 4) return sdp;

  const head = parts.slice(0, 3);
  const payloads = parts.slice(3);

  const preferred = [PCMA, PCMU].filter((payload) => payloads.includes(payload));

  if (preferred.length === 0) return sdp;

  lines[index] = [
    ...head,
    ...preferred,
    ...payloads.filter((payload) => !preferred.includes(payload)),
  ].join(' ');

  return lines.join('\r\n');
}
